/**
 * RemediationSystemsPanel — Declare active remediation systems for a site.
 *
 * Lists active systems, add/delete with technology dropdown.
 * MDPE systems support event-based pricing: 12hr, 24hr, or monthly schedule.
 * Each system maps to rate catalog for monthly cost calculation.
 * Source field supports future "recommended" systems from Dev 2's recommendation engine.
 */
import { useEffect, useState } from 'react';
import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { FadeIn } from '../animations/FadeIn';
import {
  fetchRemediationSystems,
  createRemediationSystem,
  updateRemediationSystem,
  deleteRemediationSystem,
} from '../../api/polygon';
import type { RemediationSystemItem, MdpeConfig } from '../../api/polygon';

const TECHNOLOGIES = [
  { value: 'mdpe', label: 'MDPE' },
  { value: 'solar_skimmer', label: 'Solar Skimmer' },
  { value: 'pump_and_treat', label: 'Pump & Treat' },
  { value: 'air_sparging', label: 'Air Sparging' },
  { value: 'sve', label: 'SVE' },
  { value: 'isco', label: 'ISCO' },
  { value: 'bioremediation', label: 'Bioremediation' },
  { value: 'mna', label: 'MNA' },
] as const;

// Plains Amendment 2025 rates (defaults — backend uses DB values)
const MDPE_RATES = {
  '12hr': 4900,
  '24hr': 6200,
  'monthly': 22000,
} as const;

const MDPE_SCHEDULES = [
  { value: '12hr', label: '12hr Event', rate: MDPE_RATES['12hr'], unit: '/event' },
  { value: '24hr', label: '24hr Event', rate: MDPE_RATES['24hr'], unit: '/event' },
  { value: 'monthly', label: 'Monthly', rate: MDPE_RATES['monthly'], unit: '/mo' },
] as const;

// Ancillary cost defaults (client-side preview only; backend computes authoritative values)
const ANCILLARY_DEFAULTS = {
  tedlar_rate: 18,
  tedlar_per_event: 8,
  per_diem_rate: 270,
  fuel_rate: 200,
  lab_monthly: 750,
};

function techLabel(tech: string): string {
  return TECHNOLOGIES.find(t => t.value === tech)?.label || tech.replace(/_/g, ' ');
}

function formatCurrency(value: number): string {
  return `$${Math.round(value).toLocaleString()}`;
}

/** Client-side cost preview for MDPE event config */
function estimateMdpeMonthlyCost(config: MdpeConfig): number {
  if (config.event_duration === 'monthly') {
    return MDPE_RATES.monthly;
  }
  const eventRate = MDPE_RATES[config.event_duration];
  const equipment = config.events_per_month * eventRate;
  const tedlar = config.events_per_month * config.tedlar_bags_per_event * ANCILLARY_DEFAULTS.tedlar_rate;
  const perDiem = config.include_per_diem ? config.events_per_month * ANCILLARY_DEFAULTS.per_diem_rate : 0;
  const fuel = config.include_fuel ? config.events_per_month * ANCILLARY_DEFAULTS.fuel_rate : 0;
  return equipment + tedlar + perDiem + fuel + ANCILLARY_DEFAULTS.lab_monthly;
}

/** Get MDPE schedule summary label */
function mdpeScheduleLabel(config: MdpeConfig): string {
  if (config.event_duration === 'monthly') return 'Monthly';
  return `${config.events_per_month} x ${config.event_duration}`;
}

const DEFAULT_MDPE_CONFIG: MdpeConfig = {
  event_duration: '24hr',
  events_per_month: 3,
  tedlar_bags_per_event: 8,
  include_per_diem: true,
  include_fuel: true,
};

interface MdpeConfigFormProps {
  config: MdpeConfig;
  onChange: (config: MdpeConfig) => void;
  compact?: boolean;
}

/** Inline MDPE schedule configuration form */
function MdpeConfigForm({ config, onChange, compact }: MdpeConfigFormProps) {
  const isMonthly = config.event_duration === 'monthly';
  const estimated = estimateMdpeMonthlyCost(config);

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3 mt-2'}>
      {/* Schedule type toggle */}
      <div className="flex items-center gap-1">
        {MDPE_SCHEDULES.map(s => (
          <button
            key={s.value}
            onClick={() => onChange({ ...config, event_duration: s.value as MdpeConfig['event_duration'] })}
            className={`px-2.5 py-1 rounded text-[10px] tracking-wider transition-colors ${
              config.event_duration === s.value
                ? 'bg-cyan-900/60 text-cyan-300 border border-cyan-700'
                : 'bg-mono-800 text-mono-500 border border-mono-700 hover:text-mono-300'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Event-based options */}
      {!isMonthly && (
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-mono-500 text-[10px] tracking-wider">EVENTS/MO</span>
            <div className="flex items-center border border-mono-700 rounded">
              <button
                onClick={() => onChange({ ...config, events_per_month: Math.max(1, config.events_per_month - 1) })}
                className="px-2 py-0.5 text-mono-400 hover:text-mono-200 text-xs"
              >
                -
              </button>
              <span className="px-2 py-0.5 text-mono-200 text-xs font-semibold min-w-[24px] text-center bg-mono-800">
                {config.events_per_month}
              </span>
              <button
                onClick={() => onChange({ ...config, events_per_month: Math.min(10, config.events_per_month + 1) })}
                className="px-2 py-0.5 text-mono-400 hover:text-mono-200 text-xs"
              >
                +
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-mono-500 text-[10px] tracking-wider">TEDLAR</span>
            <div className="flex items-center border border-mono-700 rounded">
              <button
                onClick={() => onChange({ ...config, tedlar_bags_per_event: Math.max(0, config.tedlar_bags_per_event - 1) })}
                className="px-2 py-0.5 text-mono-400 hover:text-mono-200 text-xs"
              >
                -
              </button>
              <span className="px-2 py-0.5 text-mono-200 text-xs font-semibold min-w-[24px] text-center bg-mono-800">
                {config.tedlar_bags_per_event}
              </span>
              <button
                onClick={() => onChange({ ...config, tedlar_bags_per_event: Math.min(20, config.tedlar_bags_per_event + 1) })}
                className="px-2 py-0.5 text-mono-400 hover:text-mono-200 text-xs"
              >
                +
              </button>
            </div>
            <span className="text-mono-600 text-[10px]">/event</span>
          </div>
        </div>
      )}

      {/* Cost preview */}
      <div className="flex items-center justify-between bg-mono-800/50 rounded px-3 py-2">
        <span className="text-mono-500 text-[10px] tracking-wider">EST. MONTHLY</span>
        <div className="text-right">
          <span className="text-mono-100 text-sm font-semibold font-orbitron">{formatCurrency(estimated)}</span>
          <span className="text-mono-500 text-xs">/mo</span>
          {!isMonthly && (
            <div className="text-mono-600 text-[9px]">
              {config.events_per_month} x {formatCurrency(MDPE_RATES[config.event_duration])} + ancillary
            </div>
          )}
          {isMonthly && (
            <div className="text-mono-600 text-[9px]">
              Plains ICE Technologies flat rate (excl. O&M)
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface RemediationSystemsPanelProps {
  siteId: string;
  onSystemsChanged?: () => void;
}

export function RemediationSystemsPanel({ siteId, onSystemsChanged }: RemediationSystemsPanelProps) {
  const [systems, setSystems] = useState<RemediationSystemItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newTech, setNewTech] = useState('mdpe');
  const [mdpeConfig, setMdpeConfig] = useState<MdpeConfig>({ ...DEFAULT_MDPE_CONFIG });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editConfig, setEditConfig] = useState<MdpeConfig>({ ...DEFAULT_MDPE_CONFIG });

  async function loadSystems() {
    try {
      setLoading(true);
      const resp = await fetchRemediationSystems(siteId);
      setSystems(resp.systems);
    } catch (err) {
      console.error('Failed to load remediation systems:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (siteId) loadSystems();
  }, [siteId]);

  async function handleAdd() {
    setSaving(true);
    try {
      const payload: {
        technology: string;
        status?: string;
        monthly_operating_cost?: number;
        extra_data?: Record<string, any>;
      } = { technology: newTech, status: 'active' };
      if (newTech === 'mdpe') {
        // For monthly schedule, don't send mdpe_config — use flat rate
        if (mdpeConfig.event_duration === 'monthly') {
          payload.monthly_operating_cost = MDPE_RATES.monthly;
        } else {
          payload.extra_data = { mdpe_config: mdpeConfig };
        }
      }
      await createRemediationSystem(siteId, payload);
      await loadSystems();
      setAdding(false);
      setNewTech('mdpe');
      setMdpeConfig({ ...DEFAULT_MDPE_CONFIG });
      onSystemsChanged?.();
    } catch (err) {
      console.error('Failed to create system:', err);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(systemId: string) {
    setSaving(true);
    try {
      await deleteRemediationSystem(systemId);
      await loadSystems();
      onSystemsChanged?.();
    } catch (err) {
      console.error('Failed to delete system:', err);
    } finally {
      setSaving(false);
    }
  }

  function startEditing(sys: RemediationSystemItem) {
    const existing = sys.extra_data?.mdpe_config;
    if (existing) {
      setEditConfig({ ...DEFAULT_MDPE_CONFIG, ...existing });
    } else {
      // System uses flat rate (monthly or legacy $22K)
      setEditConfig({ ...DEFAULT_MDPE_CONFIG, event_duration: 'monthly' });
    }
    setEditingId(sys.id);
  }

  async function handleSaveEdit() {
    if (!editingId) return;
    setSaving(true);
    try {
      if (editConfig.event_duration === 'monthly') {
        await updateRemediationSystem(editingId, {
          monthly_operating_cost: MDPE_RATES.monthly,
          extra_data: { mdpe_config: null },
        } as any);
      } else {
        await updateRemediationSystem(editingId, {
          monthly_operating_cost: null,
          extra_data: { mdpe_config: editConfig },
        } as any);
      }
      await loadSystems();
      setEditingId(null);
      onSystemsChanged?.();
    } catch (err) {
      console.error('Failed to update system:', err);
    } finally {
      setSaving(false);
    }
  }

  const activeSystems = systems.filter(s => s.status === 'active');

  /** Get display cost for a system */
  function getSystemMonthlyCost(sys: RemediationSystemItem): number | null {
    if (sys.monthly_operating_cost != null && sys.monthly_operating_cost > 0) {
      return sys.monthly_operating_cost;
    }
    // Estimate from MDPE config
    const cfg = sys.extra_data?.mdpe_config;
    if (sys.technology === 'mdpe' && cfg) {
      return estimateMdpeMonthlyCost(cfg as MdpeConfig);
    }
    return null;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-mono-500 tracking-wider">ACTIVE REMEDIATION SYSTEMS</h4>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            ADD SYSTEM
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-mono-600 text-xs py-4 text-center">Loading systems...</div>
      ) : (
        <div className="space-y-2">
          {activeSystems.length === 0 && !adding && (
            <p className="text-mono-600 text-xs py-2">
              No active remediation systems declared. Add systems to calculate remediation costs.
            </p>
          )}

          {activeSystems.map(sys => {
            const isEditing = editingId === sys.id;
            const monthlyCost = getSystemMonthlyCost(sys);
            const cfg = sys.extra_data?.mdpe_config as MdpeConfig | undefined;

            return (
              <div key={sys.id} className="bg-mono-850 rounded px-4 py-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-mono-200 text-xs font-semibold">{techLabel(sys.technology)}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] tracking-wider ${
                      sys.source === 'recommended'
                        ? 'bg-amber-900/40 text-amber-400 border border-amber-800'
                        : 'bg-cyan-900/40 text-cyan-400 border border-cyan-800'
                    }`}>
                      {sys.source === 'recommended' ? 'RECOMMENDED' : 'DECLARED'}
                    </span>
                    {/* MDPE schedule summary */}
                    {sys.technology === 'mdpe' && cfg && !isEditing && (
                      <span className="text-mono-400 text-[10px]">{mdpeScheduleLabel(cfg)}/mo</span>
                    )}
                    {sys.technology === 'mdpe' && !cfg && !isEditing && (
                      <span className="text-mono-400 text-[10px]">Monthly flat</span>
                    )}
                    {sys.install_date && (
                      <span className="text-mono-600 text-[10px]">since {sys.install_date}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {monthlyCost != null && !isEditing && (
                      <span className="text-mono-200 text-xs font-semibold">{formatCurrency(monthlyCost)}/mo</span>
                    )}
                    {sys.technology === 'mdpe' && !isEditing && (
                      <button
                        onClick={() => startEditing(sys)}
                        disabled={saving}
                        className="p-1 text-mono-600 hover:text-cyan-400 transition-colors"
                        title="Edit MDPE schedule"
                      >
                        <PencilSquareIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {!isEditing && (
                      <button
                        onClick={() => handleDelete(sys.id)}
                        disabled={saving}
                        className="p-1 text-mono-600 hover:text-mono-300 transition-colors"
                        title="Remove system"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline edit form for MDPE */}
                {isEditing && (
                  <FadeIn>
                    <div className="mt-2 pt-2 border-t border-mono-800">
                      <MdpeConfigForm config={editConfig} onChange={setEditConfig} compact />
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={handleSaveEdit}
                          disabled={saving}
                          className="flex items-center gap-1 px-3 py-1 bg-cyan-900/50 text-cyan-400 border border-cyan-700 rounded text-[10px] tracking-wider hover:bg-cyan-900/70 transition-colors disabled:opacity-50"
                        >
                          <CheckIcon className="h-3 w-3" />
                          {saving ? 'SAVING...' : 'SAVE'}
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="flex items-center gap-1 px-3 py-1 bg-mono-800 text-mono-400 border border-mono-700 rounded text-[10px] tracking-wider hover:text-mono-200 transition-colors"
                        >
                          <XMarkIcon className="h-3 w-3" />
                          CANCEL
                        </button>
                      </div>
                    </div>
                  </FadeIn>
                )}
              </div>
            );
          })}

          {/* Add system form */}
          {adding && (
            <FadeIn>
              <div className="bg-mono-850 rounded px-4 py-3">
                <div className="flex items-center gap-2">
                  <select
                    value={newTech}
                    onChange={e => setNewTech(e.target.value)}
                    className="bg-mono-800 border border-mono-600 rounded px-2 py-1 text-mono-200 text-xs focus:outline-none focus:border-cyan-500"
                  >
                    {TECHNOLOGIES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>

                  {newTech !== 'mdpe' && (
                    <>
                      <button
                        onClick={handleAdd}
                        disabled={saving}
                        className="px-3 py-1 bg-cyan-900/50 text-cyan-400 border border-cyan-700 rounded text-xs tracking-wider hover:bg-cyan-900/70 transition-colors disabled:opacity-50"
                      >
                        {saving ? 'ADDING...' : 'ADD'}
                      </button>
                      <button
                        onClick={() => { setAdding(false); setNewTech('mdpe'); }}
                        className="px-3 py-1 bg-mono-800 text-mono-400 border border-mono-700 rounded text-xs tracking-wider hover:text-mono-200 transition-colors"
                      >
                        CANCEL
                      </button>
                    </>
                  )}
                </div>

                {/* MDPE schedule config */}
                {newTech === 'mdpe' && (
                  <>
                    <MdpeConfigForm config={mdpeConfig} onChange={setMdpeConfig} />
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={handleAdd}
                        disabled={saving}
                        className="px-3 py-1 bg-cyan-900/50 text-cyan-400 border border-cyan-700 rounded text-xs tracking-wider hover:bg-cyan-900/70 transition-colors disabled:opacity-50"
                      >
                        {saving ? 'ADDING...' : 'ADD'}
                      </button>
                      <button
                        onClick={() => { setAdding(false); setNewTech('mdpe'); setMdpeConfig({ ...DEFAULT_MDPE_CONFIG }); }}
                        className="px-3 py-1 bg-mono-800 text-mono-400 border border-mono-700 rounded text-xs tracking-wider hover:text-mono-200 transition-colors"
                      >
                        CANCEL
                      </button>
                    </div>
                  </>
                )}
              </div>
            </FadeIn>
          )}
        </div>
      )}
    </div>
  );
}
